/**
 * Address field with DaData suggestions. On select fills address + latitude + longitude from suggestion.
 * Styled via theme (AddressDaDataField) to match MuiOutlinedInput and MuiMenu dropdown.
 */
import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import { styled } from '@mui/material/styles';
import DadataSuggestions from 'react-dadata-suggestions';
import 'react-dadata-suggestions/dist/styles.css';

const DADATA_TOKEN = 'd395d2fe1619a04a7f414de8d730b54a1fccf9a2';

const Root = styled(Box, { name: 'AddressDaDataField' })(({ theme }) => {
  const rootStyles = theme.components?.AddressDaDataField?.styleOverrides?.root;
  return typeof rootStyles === 'function' ? rootStyles({ theme }) : rootStyles || {};
});

export default function AddressDaDataField({
  value,
  onChange,
  onSelect,
  error,
  helperText,
  label,
  placeholder,
  disabled,
}) {
  const handleChange = React.useCallback(
    (queryString) => {
      if (onChange) onChange(queryString);
    },
    [onChange],
  );

  const handleSelect = React.useCallback(
    (suggestion) => {
      if (!suggestion) return;
      const address = suggestion.value ?? '';
      const latitude = suggestion.data?.geo_lat ?? '';
      const longitude = suggestion.data?.geo_lon ?? '';
      if (onSelect) onSelect({ address, latitude, longitude });
    },
    [onSelect],
  );

  return (
    <FormControl fullWidth error={!!error} disabled={disabled} variant="outlined" sx={{ display: 'block' }}>
      {label && (
        <InputLabel shrink sx={{ mb: 0.5, display: 'block' }}>
          {label}
        </InputLabel>
      )}
      <Root data-error={!!error}>
        <DadataSuggestions
          token={DADATA_TOKEN}
          service="address"
          query={value ?? ''}
          placeholder={placeholder ?? label ?? 'Start typing address…'}
          onChange={handleChange}
          onSelect={handleSelect}
          count={10}
          minChars={3}
        />
      </Root>
      {helperText != null && (
        <FormHelperText sx={{ mt: 0.5 }} error={!!error}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

AddressDaDataField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};
